import React from 'react';
import { Box, Container, Typography, Button, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StyleIcon from '@mui/icons-material/Style';
import config from '../config.json';

const { hero } = config;

function Hero() {
  const theme = useTheme();

  const scrollTo = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Box
      id="home"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        pt: { xs: 8, md: 0 },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-50%',
          left: '-20%',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: theme.custom.heroGlow1,
          filter: 'blur(60px)',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '-30%',
          right: '-10%',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background: theme.custom.heroGlow2,
          filter: 'blur(60px)',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            gap: { xs: 6, md: 8 },
          }}
        >
          <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: 3,
                mb: 2,
                display: 'block',
                fontSize: '0.85rem',
              }}
            >
              {hero.overline}
            </Typography>
            <Typography variant="h1" sx={{ mb: 3 }}>
              {hero.headingLine1}
              <Box
                component="span"
                sx={{
                  display: 'block',
                  background: theme.custom.gradientPrimary,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {hero.headingLine2}
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                mb: 4,
                maxWidth: 520,
                mx: { xs: 'auto', md: 0 },
                fontSize: '1.15rem',
              }}
            >
              {hero.description}
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent={{ xs: 'center', md: 'flex-start' }}
            >
              <Button
                component={RouterLink}
                to="/cardfolio"
                variant="contained"
                color="primary"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{ px: 4, py: 1.5 }}
              >
                {hero.primaryButton}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={() => scrollTo('#about')}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderWidth: 2,
                  '&:hover': { borderWidth: 2 },
                }}
              >
                {hero.secondaryButton}
              </Button>
            </Stack>
          </Box>

          {/* Hero Visual - Card illustration */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                width: { xs: 280, sm: 340, md: 400 },
                height: { xs: 280, sm: 340, md: 400 },
                borderRadius: '50%',
                background: theme.custom.heroCircleBg,
                border: theme.custom.heroCircleBorder,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                animation: 'float 6s ease-in-out infinite',
                '@keyframes float': {
                  '0%, 100%': { transform: 'translateY(0px)' },
                  '50%': { transform: 'translateY(-20px)' },
                },
              }}
            >
              <StyleIcon
                sx={{
                  fontSize: { xs: 100, md: 140 },
                  color: 'primary.main',
                  opacity: 0.9,
                }}
              />
              {/* Orbiting dots */}
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    position: 'absolute',
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background:
                      i === 0
                        ? theme.custom.orb1
                        : i === 1
                        ? theme.custom.orb2
                        : theme.custom.orb3,
                    top: i === 0 ? '10%' : i === 1 ? '80%' : '50%',
                    left: i === 0 ? '80%' : i === 1 ? '15%' : '95%',
                    animation: `pulse ${2 + i * 0.5}s ease-in-out infinite`,
                    '@keyframes pulse': {
                      '0%, 100%': {
                        transform: 'scale(1)',
                        opacity: 1,
                      },
                      '50%': {
                        transform: 'scale(1.5)',
                        opacity: 0.5,
                      },
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default Hero;
